// ============================================================================
// PORTAL FINANCEIRO REPOSITORY
// ARQUIVO: src/services/PortalFinanceiroRepository.js
// RESPONSABILIDADE: Operações Firestore do PortalFinanceiro (invoices, ledger).
// Criado: Fase 6.3 — mover Firestore direto de PortalFinanceiro.jsx para repositório.
// ============================================================================

import { collection, doc, limit, onSnapshot, query, serverTimestamp, where, writeBatch } from 'firebase/firestore';
import { db } from '../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../config/Config';

const APP_ID = FIREBASE_CONFIG?.projectId || 'portalrda-f312d';

/** Retorna fontes de invoices (root + artifacts). */
export function getInvoiceSources(appId) {
  const sources = [];
  sources.push({ key: 'root', ref: collection(db, 'invoices') });
  if (appId) {
    sources.push({ key: 'artifacts', ref: collection(db, 'artifacts', appId, 'public', 'data', 'invoices') });
  }
  return sources;
}

/** Retorna referência ao documento invoice. */
export function getInvoiceRef(sourceKey, appId, invoiceId) {
  return sourceKey === 'artifacts' && appId
    ? doc(db, 'artifacts', appId, 'public', 'data', 'invoices', String(invoiceId))
    : doc(db, 'invoices', String(invoiceId));
}

/** Registra pagamento no batch (invoice + ledger). */
export function buildPaymentBatch(invoiceRef, ledgerPayload, appId) {
  const batch = writeBatch(db);
  batch.update(invoiceRef, { status: 'PAID', paidAt: serverTimestamp() });

  const rootLedgerRef = doc(collection(db, 'financial_ledger'));
  batch.set(rootLedgerRef, ledgerPayload);

  if (appId) {
    const artifactsLedgerRef = doc(collection(db, 'artifacts', appId, 'public', 'data', 'financial_ledger'));
    batch.set(artifactsLedgerRef, ledgerPayload);
  }

  return batch;
}

/** Subscreve a invoices do cliente (root + artifacts). */
export function subscribeClientInvoices(clientId, appId, onData, onError) {
  const sources = [];
  sources.push({ key: 'root', ref: collection(db, 'invoices') });
  if (appId) {
    sources.push({ key: 'artifacts', ref: collection(db, 'artifacts', appId, 'public', 'data', 'invoices') });
  }

  const maps = sources.map(() => new Map());
  const ready = sources.map(() => false);
  const unsubscribers = [];

  const recompute = () => {
    const merged = new Map();
    for (let i = sources.length - 1; i >= 0; i -= 1) {
      maps[i].forEach((v, k) => merged.set(k, v));
    }
    onData(Array.from(merged.values()));
  };

  sources.forEach((s, idx) => {
    const qq = query(s.ref, where('clientId', '==', clientId), limit(500));
    const unsub = onSnapshot(
      qq,
      (snap) => {
        const next = new Map();
        snap.docs.forEach((d) => next.set(d.id, { id: d.id, __source: s.key, ...(d.data() || {}) }));
        maps[idx] = next;
        ready[idx] = true;
        recompute();
      },
      (e) => {
        ready[idx] = true;
        onError && onError(e);
        recompute();
      }
    );
    unsubscribers.push(unsub);
  });

  return () => {
    unsubscribers.forEach((u) => { try { u && u(); } catch {} });
  };
}
